import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiEye,
  FiX,
  FiSave,
  FiUserCheck,
  FiUserX,
  FiUserPlus,
  FiDatabase,
} from "react-icons/fi";
import { Users as UsersIcon } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import apiService from "../services/api";
import PageTransition from "../components/PageTransition";
import ProvisioningProgressModal from "../components/ProvisioningProgressModal";
import DeleteProgressModal from "../components/DeleteProgressModal";
import CheckTablesModal from "../components/CheckTablesModal";
import { ModernHeader, ModernCard } from "../components/Modern";

const Users = () => {
  const { isDarkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [showCheckTablesModal, setShowCheckTablesModal] = useState(false);
  const [checkTablesResult, setCheckTablesResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    role: "user",
    target_db_name: "",
    redis_db: "",
    is_admin: false,
    active: true,
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      setUsers(response.users || []);
      setError("");
    } catch (error) {
      setError("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetForm = () => {
    setFormData({
      name: "",
      username: "",
      email: "",
      password: "",
      role: "user",
      target_db_name: "",
      redis_db: "",
      is_admin: false,
      active: true,
    });
  };

  const handleCheckTables = async (user) => {
    setCheckTablesResult(null);
    setShowCheckTablesModal(true);
    try {
      const res = await apiService.checkTables(user.id);
      setCheckTablesResult({ ...res, userId: user.id });
    } catch (error) {
      console.error("Erro ao verificar tabelas:", error);
      setCheckTablesResult({
        error: error.message || "Erro ao verificar tabelas do usuário",
        userId: user.id
      });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      console.log(
        "🎯 [Frontend] Dados do formulário antes de enviar:",
        formData,
      );

      // Abre modal imediatamente e mostra estado inicial de processamento
      setShowProvisioningModal(true);
      setProvisioningResult({
        provisioning: {
          steps: [
            {
              type: "init",
              target: formData.target_db_name || "-",
              result: {
                message: "Iniciando criação de usuário e provisionamento...",
              },
            },
          ],
          errors: [],
          success: true,
        },
        workflows: { success: false, totalDeployed: 0, workflows: [] },
      });

      const res = await apiService.createUser(formData);

      // Abrir modal de progresso com detalhes de provisionamento e workflows
      setProvisioningResult(res);
      setShowProvisioningModal(true);

      setSuccess("Usuário criado com sucesso!");
      setShowCreateModal(false);
      resetForm();
      loadUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("❌ [Frontend] Erro ao criar usuário:", error);
      console.error("📦 [Frontend] Resposta do erro:", error.response?.data);

      // Exibir erro também no modal de provisionamento
      setProvisioningResult({
        provisioning: {
          steps: [],
          errors: [
            error.response?.data?.message ||
            error.message ||
            "Erro ao criar usuário",
          ],
          success: false,
        },
        workflows: {
          success: false,
          error: error.response?.data?.message || error.message,
        },
      });
      setShowProvisioningModal(true);

      setError(error.response?.data?.message || "Erro ao criar usuário");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
      const updateData = { ...formData };

      if (!updateData.password) {
        delete updateData.password;
      }

      await apiService.updateUser(selectedUser.id, updateData);

      setSuccess("Usuário atualizado com sucesso!");
      setShowEditModal(false);
      resetForm();
      loadUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      setError(error.response?.data?.message || "Erro ao atualizar usuário");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || isDeleting) return;
    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");
      const res = await apiService.deleteUser(userToDelete.id);
      // Mostrar modal de progresso de exclusão com detalhes
      setDeleteResult(res?.details || res);
      setShowDeleteProgressModal(true);
      setShowDeleteModal(false);
      setUserToDelete(null);
      // Atualizar lista após exclusão
      await loadUsers();
    } catch (error) {
      // Tratar 404 como exclusão idempotente (usuário já não existe)
      if (error?.response?.status === 404) {
        setDeleteResult({
          success: true,
          userDeleted: false,
          message: "Usuário já não existe",
        });
        setShowDeleteProgressModal(true);
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        const msg =
          error?.response?.data?.message ||
          error.message ||
          "Erro ao excluir usuário";
        setDeleteErrorMessage(msg);
      }
    } finally {
      setIsDeleting(false);
      // Sempre atualizar a lista para refletir estado real do backend
      await loadUsers();
    }
  };

  // Estado e modal para progresso de provisionamento
  const [showProvisioningModal, setShowProvisioningModal] = useState(false);
  const [provisioningResult, setProvisioningResult] = useState(null);
  const [showDeleteProgressModal, setShowDeleteProgressModal] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  const toggleUserStatus = async (userId, currentStatus) => {
    if (togglingUserId === userId) return; // Previne cliques múltiplos

    try {
      setTogglingUserId(userId);
      await apiService.toggleUserStatus(userId);
      setSuccess(
        `Usuário ${!currentStatus ? "ativado" : "desativado"} com sucesso!`,
      );
      loadUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      setError("Erro ao alterar status do usuário");
      setTimeout(() => setError(""), 3000);
    } finally {
      setTogglingUserId(null);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);

    setFormData({
      name: user.name || "",
      username: user.username || "",
      email: user.email || "",
      password: "",
      role: user.role || "user",
      target_db_name: user.target_db_name || "",
      is_admin: user.is_admin || false,
      active: user.active !== undefined ? user.active : true,
    });
    setShowEditModal(true);
  };

  const openViewModal = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Filtros
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.active) ||
      (statusFilter === "inactive" && !user.active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Paginação
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const getRoleColor = (role) => {
    if (isDarkMode) {
      switch (role) {
        case "admin":
          return "bg-red-900/30 text-red-300";
        case "manager":
          return "bg-blue-900/30 text-blue-300";
        default:
          return "bg-gray-700 text-gray-300";
      }
    } else {
      switch (role) {
        case "admin":
          return "bg-red-100 text-red-800";
        case "manager":
          return "bg-blue-100 text-blue-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    }
  };

  const getStatusColor = (active) => {
    if (isDarkMode) {
      return active
        ? "bg-green-900/30 text-green-300"
        : "bg-red-900/30 text-red-300";
    } else {
      return active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
    }
  };

  // Estatísticas (disponível para uso futuro)
  // eslint-disable-next-line no-unused-vars
  const stats = {
    total: users.length,
    active: users.filter((u) => u.active).length,
    inactive: users.filter((u) => !u.active).length,
    admins: users.filter((u) => u.role === "admin").length,
  };

  return (
    <PageTransition>
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="max-w-7xl mx-auto space-y-8">
          <ModernHeader
            title="Gerenciamento de Usuários"
            subtitle="Gerencie usuários, permissões e configurações do sistema de forma centralizada"
            icon={UsersIcon}
            themeColor="green"
          />

          {/* Mensagens de Erro e Sucesso */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg shadow-sm animate-slideInDown">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg shadow-sm animate-slideInDown">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              </div>
            </div>
          )}



          {/* Filtros e Busca */}
          <ModernCard
            isDarkMode={isDarkMode}
            themeColor="green"
            className="p-6"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar usuários..."
                    className={`pl-10 pr-4 py-2 border rounded-lg w-full sm:w-64 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <select
                  className={`px-4 py-2 border rounded-lg ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">Todas as funções</option>
                  <option value="admin">Administrador</option>
                  <option value="user">Usuário</option>
                </select>

                <select
                  className={`px-4 py-2 border rounded-lg ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center gap-2 transition-all shadow-md"
              >
                <FiPlus className="w-5 h-5" />
                Novo Usuário
              </button>
            </div>
          </ModernCard>

          {/* Tabela de Usuários */}
          {loading ? (
            <ModernCard
              isDarkMode={isDarkMode}
              themeColor="green"
              className="p-8"
            >
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                <span
                  className={`ml-3 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  Carregando usuários...
                </span>
              </div>
            </ModernCard>
          ) : (
            <>
              <ModernCard
                isDarkMode={isDarkMode}
                themeColor="green"
                className="overflow-hidden"
              >
                {/* Dica de scroll horizontal */}
                <div
                  className={`px-4 py-2 text-xs ${isDarkMode ? "text-gray-400 bg-gray-800" : "text-gray-600 bg-gray-50"} border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex items-center justify-center`}
                >
                  <span>
                    💡 Deslize horizontalmente para ver todas as colunas e ações
                  </span>
                </div>
                <div className="relative">
                  <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                    <table
                      className={`min-w-full divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}
                      style={{ minWidth: "1000px" }}
                    >
                      <thead
                        className={`${isDarkMode ? "bg-gray-800" : "bg-green-50"}`}
                      >
                        <tr>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "200px" }}
                          >
                            Usuário
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "200px" }}
                          >
                            Email
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "120px" }}
                          >
                            Função
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "100px" }}
                          >
                            Status
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "120px" }}
                          >
                            DB Target
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "120px" }}
                          >
                            Criado em
                          </th>
                          <th
                            className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            style={{ minWidth: "160px" }}
                          >
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody
                        className={`${isDarkMode ? "bg-gray-700 divide-y divide-gray-600" : "bg-white divide-y divide-gray-200"}`}
                      >
                        {currentUsers.map((user) => (
                          <tr
                            key={user.id}
                            className={`transition-colors ${isDarkMode ? "hover:bg-gray-600" : "hover:bg-green-50"}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-green-600 flex items-center justify-center">
                                    <span className="text-sm font-medium text-white">
                                      {user.name
                                        ? user.name.charAt(0).toUpperCase()
                                        : user.username.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div
                                    className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {user.name || "Sem nome"}
                                  </div>
                                  <div
                                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                                  >
                                    @{user.username}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {user.email}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}
                              >
                                {user.role === "admin"
                                  ? "Administrador"
                                  : "Usuário"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.active)}`}
                              >
                                {user.active ? "Ativo" : "Inativo"}
                              </span>
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            >
                              {user.target_db_name || "Não definido"}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-500"}`}
                            >
                              {new Date(user.created_at).toLocaleDateString(
                                "pt-BR",
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openViewModal(user)}
                                  className={`${isDarkMode ? "text-green-400 hover:text-green-300" : "text-green-600 hover:text-green-800"}`}
                                  title="Visualizar"
                                >
                                  <FiEye />
                                </button>
                                <button
                                  onClick={() => openEditModal(user)}
                                  className={`${isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}`}
                                  title="Editar"
                                >
                                  <FiEdit2 />
                                </button>

                                <button
                                  onClick={() => handleCheckTables(user)}
                                  className={`${isDarkMode ? "text-purple-400 hover:text-purple-300" : "text-purple-600 hover:text-purple-800"}`}
                                  title="Verificar Tabelas"
                                >
                                  <FiDatabase />
                                </button>

                                <button
                                  onClick={() =>
                                    toggleUserStatus(user.id, user.active)
                                  }
                                  disabled={togglingUserId === user.id}
                                  className={`relative transition-all duration-200 ${togglingUserId === user.id
                                    ? isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                    : user.active
                                      ? isDarkMode
                                        ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                        : "text-red-600 hover:text-red-800 hover:bg-red-50"
                                      : isDarkMode
                                        ? "text-green-400 hover:text-green-300 hover:bg-green-900/20"
                                        : "text-green-600 hover:text-green-800 hover:bg-green-50"
                                    } ${togglingUserId === user.id ? "cursor-not-allowed" : "cursor-pointer"} p-1 rounded`}
                                  title={
                                    togglingUserId === user.id
                                      ? "Processando..."
                                      : user.active
                                        ? "Desativar usuário"
                                        : "Ativar usuário"
                                  }
                                >
                                  {togglingUserId === user.id ? (
                                    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                                  ) : user.active ? (
                                    <FiUserX />
                                  ) : (
                                    <FiUserCheck />
                                  )}
                                </button>
                                <button
                                  onClick={() => openDeleteModal(user)}
                                  className={`${isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-800"}`}
                                  title="Deletar"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Indicador de scroll horizontal */}
                  <div
                    className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l ${isDarkMode ? "from-gray-700 to-transparent" : "from-white to-transparent"} opacity-50`}
                  ></div>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div
                    className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} px-4 py-3 flex items-center justify-between border-t`}
                  >
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border ${isDarkMode ? "border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600" : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"} text-sm font-medium rounded-md disabled:opacity-50`}
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className={`ml-3 relative inline-flex items-center px-4 py-2 border ${isDarkMode ? "border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600" : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"} text-sm font-medium rounded-md disabled:opacity-50`}
                      >
                        Próximo
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Página{" "}
                          <span className="font-medium">{currentPage}</span> de{" "}
                          <span className="font-medium">{totalPages}</span>
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${isDarkMode ? "border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600" : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"} text-sm font-medium disabled:opacity-50`}
                          >
                            Anterior
                          </button>
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i + 1}
                              onClick={() => setCurrentPage(i + 1)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1
                                ? isDarkMode
                                  ? "z-10 bg-green-900 border-green-700 text-green-300"
                                  : "z-10 bg-green-50 border-green-500 text-green-600"
                                : isDarkMode
                                  ? "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            onClick={() =>
                              setCurrentPage(
                                Math.min(totalPages, currentPage + 1),
                              )
                            }
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${isDarkMode ? "border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600" : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"} text-sm font-medium disabled:opacity-50`}
                          >
                            Próximo
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </ModernCard>
            </>
          )}
        </div>
        {/* Modal de Progresso de Provisionamento */}
        <ProvisioningProgressModal
          isOpen={showProvisioningModal}
          onClose={() => setShowProvisioningModal(false)}
          result={provisioningResult}
        />

        {/* Modal de Verificação de Tabelas */}
        <CheckTablesModal
          isOpen={showCheckTablesModal}
          onClose={() => setShowCheckTablesModal(false)}
          result={checkTablesResult}
        />

        {/* Modal de Criação */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center animate-fadeIn">
            <div
              className={`relative ${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col transform transition-all duration-300 animate-slideInUp`}
            >
              <div
                className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 ${isDarkMode ? "bg-green-900" : "bg-green-100"} rounded-lg`}
                  >
                    <FiUserPlus
                      className={`w-6 h-6 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                    />
                  </div>
                  <h3
                    className={`text-xl font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                  >
                    Criar Novo Usuário
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
                >
                  <FiX
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                </button>
              </div>

              <form
                onSubmit={handleCreateUser}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        placeholder="Digite o nome completo"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Nome de usuário *
                      </label>
                      <input
                        type="text"
                        required
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        placeholder="Digite o nome de usuário"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder="Digite o email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Senha *
                      </label>
                      <input
                        type="password"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        placeholder="Digite a senha"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Função
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({ ...formData, role: e.target.value })
                        }
                      >
                        <option value="user">Usuário</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Target DB Name
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200 focus:ring-green-600 focus:border-green-600" : "bg-white border-gray-300 text-gray-700 focus:ring-green-500 focus:border-green-500"}`}
                        value={formData.target_db_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            target_db_name: e.target.value,
                          })
                        }
                        placeholder="Nome do banco de dados"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Redis DB (1-15)
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.redis_db || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            redis_db: e.target.value,
                          })
                        }
                      >
                        <option value="">Automático</option>
                        {[...Array(15)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Status
                      </label>
                      <div className="flex items-center h-[42px]">
                        <input
                          type="checkbox"
                          id="active_create"
                          className={`h-4 w-4 rounded focus:ring-2 ${isDarkMode ? "text-green-600 focus:ring-green-500 border-gray-600 bg-gray-700" : "text-green-600 focus:ring-green-500 border-gray-300 bg-white"}`}
                          checked={formData.active}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              active: e.target.checked,
                            })
                          }
                        />
                        <label
                          htmlFor="active_create"
                          className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Usuário ativo
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Removido: Configurações Evolution API e Redis (usadas globalmente via .env) */}

                  <div className="mt-6">
                    <h4
                      className={`text-lg font-medium mb-4 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      Permissões
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_admin_create"
                          className={`h-4 w-4 ${isDarkMode ? "text-green-600 focus:ring-green-500 border-gray-600" : "text-green-600 focus:ring-green-500 border-gray-300"} rounded`}
                          checked={formData.is_admin}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_admin: e.target.checked,
                            })
                          }
                        />
                        <label
                          htmlFor="is_admin_create"
                          className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          É administrador do sistema
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`flex justify-end gap-3 p-6 border-t flex-shrink-0 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className={`px-6 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 transition-colors ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500" : "border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500"}`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 flex items-center gap-2 transition-colors ${isDarkMode ? "bg-green-700 hover:bg-green-800 focus:ring-green-600" : "bg-green-600 hover:bg-green-700 focus:ring-green-500"}`}
                  >
                    <FiSave className="w-4 h-4" />
                    Criar Usuário
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Edição */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center animate-fadeIn">
            <div
              className={`relative ${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col transform transition-all duration-300 animate-slideInUp`}
            >
              <div
                className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex-shrink-0`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 ${isDarkMode ? "bg-green-900" : "bg-green-100"} rounded-lg`}
                  >
                    <FiEdit2
                      className={`w-6 h-6 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                    />
                  </div>
                  <div>
                    <h3
                      className={`text-xl font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      Editar Usuário
                    </h3>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Editando: {selectedUser.username}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
                >
                  <FiX
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                </button>
              </div>

              <form
                onSubmit={handleUpdateUser}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        placeholder="Digite o nome completo"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Nome de usuário *
                      </label>
                      <input
                        type="text"
                        required
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        placeholder="Digite o nome de usuário"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder="Digite o email"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Função
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({ ...formData, role: e.target.value })
                        }
                      >
                        <option value="user">Usuário</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Target DB Name
                      </label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.target_db_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            target_db_name: e.target.value,
                          })
                        }
                        placeholder="Nome do banco de dados"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Redis DB (1-15)
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"}`}
                        value={formData.redis_db || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            redis_db: e.target.value,
                          })
                        }
                      >
                        <option value="">Automático</option>
                        {[...Array(15)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Status
                      </label>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="active"
                          className={`h-4 w-4 rounded focus:ring-2 ${isDarkMode ? "text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700" : "text-blue-600 focus:ring-blue-500 border-gray-300 bg-white"}`}
                          checked={formData.active}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              active: e.target.checked,
                            })
                          }
                        />
                        <label
                          htmlFor="active"
                          className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Usuário ativo
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Removido: Configurações Evolution API (usadas globalmente via .env) */}

                  <div className="mt-6">
                    <h4
                      className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Permissões
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_admin_edit"
                          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 rounded ${isDarkMode
                            ? "border-gray-600 bg-gray-700"
                            : "border-gray-300 bg-white"
                            }`}
                          checked={formData.is_admin}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_admin: e.target.checked,
                            })
                          }
                        />
                        <label
                          htmlFor="is_admin_edit"
                          className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          É administrador do sistema
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`flex justify-end gap-3 p-6 border-t flex-shrink-0 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className={`px-6 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 transition-colors ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500" : "border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500"}`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 flex items-center gap-2 transition-colors ${isDarkMode ? "bg-blue-700 hover:bg-blue-800 focus:ring-blue-600" : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"}`}
                  >
                    <FiSave className="w-4 h-4" />
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Visualização */}
        {showViewModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center animate-fadeIn">
            <div
              className={`relative rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col transform transition-all duration-300 animate-slideInUp ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
            >
              <div
                className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${isDarkMode ? "bg-green-900" : "bg-green-100"}`}
                  >
                    <FiEye
                      className={`w-6 h-6 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                    />
                  </div>
                  <div>
                    <h3
                      className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Detalhes do Usuário
                    </h3>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Visualizando: {selectedUser.username}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Nome Completo
                      </label>
                      <p
                        className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {selectedUser.name || "Não informado"}
                      </p>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Nome de usuário
                      </label>
                      <p
                        className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {selectedUser.username}
                      </p>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Email
                      </label>
                      <p
                        className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {selectedUser.email}
                      </p>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Função
                      </label>
                      <span
                        className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(selectedUser.role)}`}
                      >
                        {selectedUser.role === "admin"
                          ? "Administrador"
                          : "Usuário"}
                      </span>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Status
                      </label>
                      <span
                        className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedUser.active)}`}
                      >
                        {selectedUser.active ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Target DB Name
                      </label>
                      <p
                        className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {selectedUser.target_db_name || "Não informado"}
                      </p>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        É Administrador
                      </label>
                      <span
                        className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${selectedUser.is_admin ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-800"}`}
                      >
                        {selectedUser.is_admin ? "Sim" : "Não"}
                      </span>
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Criado em
                      </label>
                      <p
                        className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {new Date(selectedUser.created_at).toLocaleString(
                          "pt-BR",
                        )}
                      </p>
                    </div>

                    {selectedUser.updated_at && (
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Atualizado em
                        </label>
                        <p
                          className={`text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {new Date(selectedUser.updated_at).toLocaleString(
                            "pt-BR",
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Removido: Seção de configurações Evolution API na visualização */}
              </div>

              <div
                className={`flex justify-end gap-3 p-6 border-t flex-shrink-0 ${isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"}`}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    openEditModal(selectedUser);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 transition-colors"
                >
                  <FiEdit2 className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className={`px-6 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 transition-colors ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-600 focus:ring-gray-500" : "border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500"}`}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Exclusão */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center animate-fadeIn">
            <div
              className={`relative rounded-lg shadow-xl w-full max-w-md mx-4 transform transition-all duration-300 animate-slideInUp ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
            >
              {/* Overlay de Loading */}
              {isDeleting && (
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
                    <span
                      className={`${isDarkMode ? "text-gray-200" : "text-gray-700"} text-sm`}
                    >
                      Excluindo usuário...
                    </span>
                  </div>
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className={`p-3 rounded-full ${isDarkMode ? "bg-red-900" : "bg-red-100"}`}
                  >
                    <FiTrash2
                      className={`w-6 h-6 ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                    />
                  </div>
                  <div>
                    <h3
                      className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Confirmar Exclusão
                    </h3>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Esta ação não pode ser desfeita
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Tem certeza que deseja excluir o usuário{" "}
                    <strong>{userToDelete.username}</strong>? Todos os dados
                    relacionados a este usuário serão permanentemente removidos.
                  </p>
                  {deleteErrorMessage && (
                    <div className="mt-3 text-sm text-red-600">
                      {deleteErrorMessage}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      if (isDeleting) return;
                      setShowDeleteModal(false);
                      setUserToDelete(null);
                      setDeleteErrorMessage("");
                    }}
                    disabled={isDeleting}
                    className={`px-4 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 transition-colors ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500" : "border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500"} ${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    disabled={isDeleting}
                    className={`px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center gap-2 transition-colors ${isDeleting ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Excluindo...
                      </>
                    ) : (
                      <>
                        <FiTrash2 className="w-4 h-4" />
                        Excluir
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Progresso de Exclusão */}
        <DeleteProgressModal
          isOpen={showDeleteProgressModal}
          onClose={() => setShowDeleteProgressModal(false)}
          result={deleteResult}
        />

        <CheckTablesModal
          isOpen={showCheckTablesModal}
          onClose={() => setShowCheckTablesModal(false)}
          checkResult={checkTablesResult}
        />
      </div>
    </PageTransition>
  );
};

export default Users;
